import { createTheme } from "@mui/material/styles";

let theme = createTheme({
  typography: {
    fontFamily: "inherit",
  },
  breakpoints: {
    values: {
      xxs: 0,
      xs: 415,
      sm: 767,
      md: 991,
      lg: 1199,
      xl: 1919,
    },
  },
});

export const defaultThemeOptions = {
  palette: {
    primary: {
      light: "#1dcca1",
      main: "#17a17e",
      dark: "#11775e",
      contrastText: "#fff",
    },
    secondary: {
      light: "#b3c2c9",
      main: "#466472",
      dark: "#3b87ab",
      contrastText: "#fff",
    },
    pink: {
      main: "#ff448b",
      dark: "#c9366e",
      contrastText: "#fff",
    },
    orange: {
      main: "#ec720a",
      dark: "#b45808",
      contrastText: "#fff",
    },
    red: {
      main: "#b81d1d",
      dark: "#991d1d",
      contrastText: "#fff",
    },
    white: {
      main: "#ffffff",
      dark: "#ededed",
      contrastText: "#2b2b2b",
    },
    black: {
      main: "#000000",
      dark: "#333333",
      contrastText: "#ffffff",
    },
    transparent: {
      main: "rgba(0, 0, 0, 0)",
      dark: "rgba(0, 0, 0, 0.1)",
      contrastText: "#2b2b2b",
    },
    error: {
      main: "#fa2630",
    },
    background: {
      default: "#fff",
    },
  },
  components: {
    MuiTableRow: {
      styleOverrides: {
        head: {
          "& th": {
            backgroundColor: "#474544",
            color: "#ffffff",
          },
        },
        root: {
          "&:nth-of-type(even)": {
            backgroundColor: "#f9f9f9",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          color: "#2b2b2b",
          textTransform: "uppercase",
        },
        body: {
          fontSize: 14,
          padding: "0.5rem 1rem",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          background: "#ffffff",
          color: "#2b2b2b",
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          background: "#ffffff",
          border: "1px solid #d5d4d2",
          borderRadius: 4,
          "&.no-border": {
            borderColor: "transparent",
          },
          "&:hover": {
            backgroundColor: "#ffffff",
            "&:not(.Mui-disabled):before": {
              borderBottomColor: "transparent",
            },
          },
          "&.Mui-focused": {
            backgroundColor: "#ffffff",
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
          "&:before": {
            borderBottomColor: "transparent",
            left: -1,
            right: -1,
            bottom: -1,
          },
          "&:after": {
            left: -1,
            right: -1,
            bottom: -1,
          },
        },
        input: {
          "&:focus": {
            backgroundColor: "#ffffff",
            borderRadius: 4,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.no-border .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.23)",
          },
          "&.no-border:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#17a17e",
          },
          "& ::placeholder": {
            fontSize: "0.9rem",
            fontWeight: 400,
            letterSpacing: "1px",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            padding: "0 12px 0 0",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "#585756",
          fontSize: "0.9rem",
          fontWeight: 500,
          lineHeight: "2em",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          margin: 0,
          padding: "6px 5px",
          "&.Mui-error": {
            backgroundColor: "#FAE0E0",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          textAlign: "left",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            fontSize: "1.8em",
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          fontSize: "1rem",
          borderRadius: "2rem",
          padding: "0.6rem 2.5rem",
          letterSpacing: 2,
          border: "1px solid transparent",
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
        grouped: {
          boxShadow: "rgb(0 0 0 / 20%) 0px 3px 1px -2px, rgb(0 0 0 / 14%) 0px 2px 2px 0px, rgb(0 0 0 / 12%) 0px 1px 5px 0px",
          "&:hover": {
            boxShadow: "rgb(0 0 0 / 20%) 0px 3px 1px -2px, rgb(0 0 0 / 14%) 0px 2px 2px 0px, rgb(0 0 0 / 12%) 0px 1px 5px 0px",
          },
        },
      },
    },
    MuiMasonry: {
      styleOverrides: {
        root: {
          alignContent: "center",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          transform: "none",
          borderRadius: 5,
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          "&.tooltip-popper": {
            padding: "20px 30px",
            boxShadow: "0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
            borderRadius: 15,
          },
          '&[data-popper-placement="top"] .popper-arrow': {
            bottom: -46,
          },
          '&[data-popper-placement="bottom"] .popper-arrow': {
            top: -46,
            "& svg": {
              transform: "rotate(180deg)",
            },
          },
          '&[data-popper-placement="right"] .popper-arrow': {
            left: -46,
            "& svg": {
              transform: "rotate(90deg)",
            },
          },
          '&[data-popper-placement="left"] .popper-arrow': {
            right: -46,
            "& svg": {
              transform: "rotate(-90deg)",
            },
          },
          "& .popper-arrow, & .popper-arrow svg": {
            width: 46,
            height: 46,
          },
          "& .popper-arrow": {
            position: "absolute",
            visibility: "hidden",
          },
          "& .popper-arrow svg": {
            visibility: "visible",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiDialogTitle-root": {
            display: "flex",
            padding: "1rem 1.2rem 0",
            alignItems: "center",
            overflow: "visible",
            zIndex: 2,
          },
          "& .MuiDialog-paper": {
            textAlign: "center",
            width: "100%",
            borderRadius: "1rem 1rem 0 0",
            margin: "auto 0 0",
            maxHeight: "100%",
            [theme.breakpoints.up("sm")]: {
              borderRadius: "1rem",
              margin: "20px",
              maxHeight: "calc(100% - 40px)",
            },
          },
          "& .MuiDialogContent-root": {
            zIndex: 1,
            padding: "20px",
          },
          "& .dialog-title": {
            fontFamily:
              'Constantia, "Lucida Bright", Lucidabright, "Lucida Serif", Lucida, "DejaVu Serif", "Bitstream Vera Serif", "Liberation Serif", Georgia, serif',
            fontSize: "2.25rem",
            display: "block",
            marginBottom: 10,
          },
          "& .dialog-subtitle": {
            fontSize: "1rem",
            display: "block",
            marginBottom: 30,
          },
          "& .close-dialog-button": {
            marginLeft: "auto",
            marginRight: "-5px",
            cursor: "pointer",
            alignSelf: "flex-start",
            "& svg": {
              fontSize: "1rem",
              color: "#2b2b2b",
              "& path": {
                strokeWidth: 0.8,
              },
              [theme.breakpoints.up("sm")]: {
                fontSize: "1.2rem",
              },
            },
          },
        },
      },
    },
  },
};

// Default BirthdayAlarm Theme
export const defaultTheme = createTheme(theme, defaultThemeOptions);
